import { ReducerAction } from '@keplerco/core';
import { AgentDto } from '../../../../overmind/actions/ai/ai-agents';
import { ContextListDto } from '../../../../overmind/actions/ai/ai-contexts';

export enum TabIds {
  Basic = 'Basic settings',
  Advanced = 'Advanced settings',
}

export enum PillButtonIds {
  SearchContexts = 0,
  SelectedContexts = 1,
}

// state
interface AgentPageStore {
  agent: AgentDto | undefined;
  response: any;
  selectedTab: TabIds;
  selectedPillButton: PillButtonIds;
  search: string;
  availableContexts: ContextListDto[] | undefined;
  searchedContexts: ContextListDto[];
  showConfirmationModal: boolean;
}

export const initialState: AgentPageStore = {
  agent: undefined,
  response: undefined,
  selectedTab: TabIds.Basic,
  selectedPillButton: PillButtonIds.SearchContexts,
  search: '',
  availableContexts: undefined,
  searchedContexts: [],
  showConfirmationModal: false,
};

// actions
export enum AgentPageActionTypes {
  SetAgent,
  SetResponse,
  SetSelectedTab,
  SetSelectedPillButton,
  SetSearch,
  SetAvailableContexts,
  SetSearchedContexts,
  UpdateSystemPrompt,
  UpdateUserPromptIntro,
  UpdateUserPromptOutro,
  UpdateContextIds,
  SetShowConfirmationModal,
}

type AgentPageActions =
  | ReducerAction<AgentPageActionTypes.SetAgent, { payload: AgentDto | undefined }>
  | ReducerAction<AgentPageActionTypes.SetResponse, { payload: any }>
  | ReducerAction<AgentPageActionTypes.SetSelectedTab, { payload: TabIds }>
  | ReducerAction<AgentPageActionTypes.SetSelectedPillButton, { payload: PillButtonIds }>
  | ReducerAction<AgentPageActionTypes.SetSearch, { payload: string }>
  | ReducerAction<AgentPageActionTypes.SetAvailableContexts, { payload: ContextListDto[] | undefined }>
  | ReducerAction<AgentPageActionTypes.SetSearchedContexts, { payload: ContextListDto[] }>
  | ReducerAction<AgentPageActionTypes.SetShowConfirmationModal, { payload: boolean }>
  | ReducerAction<AgentPageActionTypes.UpdateSystemPrompt, { payload: string }>
  | ReducerAction<AgentPageActionTypes.UpdateUserPromptIntro, { payload: string }>
  | ReducerAction<AgentPageActionTypes.UpdateUserPromptOutro, { payload: string }>
  | ReducerAction<AgentPageActionTypes.UpdateContextIds, { payload: string[] }>;

// reducer
export function reducer(state: AgentPageStore, action: AgentPageActions): AgentPageStore {
  switch (action.type) {
    case AgentPageActionTypes.SetAgent: {
      return {
        ...state,
        agent: action.payload,
      };
    }

    case AgentPageActionTypes.SetResponse: {
      return {
        ...state,
        response: action.payload,
      };
    }

    case AgentPageActionTypes.SetSelectedTab: {
      return { ...state, selectedTab: action.payload };
    }

    case AgentPageActionTypes.SetSelectedPillButton: {
      return { ...state, selectedPillButton: action.payload };
    }

    case AgentPageActionTypes.SetSearch: {
      return { ...state, search: action.payload };
    }

    case AgentPageActionTypes.SetAvailableContexts: {
      return { ...state, availableContexts: action.payload };
    }

    case AgentPageActionTypes.SetSearchedContexts: {
      return { ...state, searchedContexts: action.payload };
    }

    case AgentPageActionTypes.UpdateSystemPrompt: {
      if (!state.agent) return state;
      return {
        ...state,
        agent: {
          ...state.agent,
          systemPromptOverride: action.payload,
        },
      };
    }

    case AgentPageActionTypes.UpdateUserPromptIntro: {
      if (!state.agent) return state;
      return {
        ...state,
        agent: {
          ...state.agent,
          userPromptTemplateOverride: {
            ...state.agent.userPromptTemplateOverride,
            intro: action.payload,
          },
        },
      };
    }

    case AgentPageActionTypes.UpdateUserPromptOutro: {
      if (!state.agent) return state;
      return {
        ...state,
        agent: {
          ...state.agent,
          userPromptTemplateOverride: {
            ...state.agent.userPromptTemplateOverride,
            outro: action.payload,
          },
        },
      };
    }

    case AgentPageActionTypes.UpdateContextIds: {
      if (!state.agent) return state;
      return {
        ...state,
        agent: {
          ...state.agent,
          contextIds: action.payload,
        },
      };
    }

    case AgentPageActionTypes.SetShowConfirmationModal: {
      return { ...state, showConfirmationModal: action.payload };
    }

    default: {
      return state;
    }
  }
}
